import _ from 'lodash';
import { Filters } from '@or-sdk/hitl';

import { HITL } from '@/api';
import {
  useSessionViewsStore,
  useSessionViewsSetters,
} from './use-session-views-store';
import { useSessionViewsRouter } from './use-session-views-router';
import { useSessionViewsMigrations } from './use-session-views-migrations';
import { ACTIVE_VERSION } from './constants';
import { UIEventBus, THROW_API_ERROR } from '@/common/ui-event-bus';

/** Most methods can switch views after applying changes */
export const useSessionViewsActions = () => {
  const store = useSessionViewsStore();
  const setters = useSessionViewsSetters();
  const { activeSessionViewId, setActiveSessionViewId } = useSessionViewsRouter();
  const { migrateSessionViewSettings } = useSessionViewsMigrations();

  const initializeSessionViews = async (options: {
    userId?: string;
    isCommon?: boolean;
    apply?: boolean;
    withCache?: <T>(request: () => Promise<T>) => Promise<T>;
  } = {}) => {
    try {
      const { userId = '', isCommon, apply, withCache = request => request() } = options;
      const views = await withCache(async () => {
        const { data } = await HITL.filters
          .getFilters({ isCommon, version: 2 }) as Filters.GetFiltersResponse;
        return data.map(item => migrateSessionViewSettings(item));
      });

      store.userId = userId;
      store.isDataReady = false;

      setters.setViews(views);
      store.isDataReady = true;

      if (apply) {
        applySessionView(activeSessionViewId.value);
      }
    } catch (error) {
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  };

  /** Apply default session view when ID or view in store are empty */
  const applySessionView = (viewId = '') => {
    viewId && store.viewsById[viewId]
      ? setActiveSessionViewId(viewId)
      : setActiveSessionViewId(store.defaultViewId);
  };

  const createSessionView = async (options: {
    data: Omit<Filters.Filter, 'id'>;
    order?: number;
    isCommon?: boolean;
    apply?: boolean;
  }) => {
    try {
      const { data, order = 0, isCommon = true, apply } = options;

      store.isUpdating = true;
      const create = { ..._.omit(data, 'version'), filterVersion: ACTIVE_VERSION };
      const response = await HITL.filters.createFilter({ ...create, isCommon, order, version: 2 });

      setters.addOrUpdateView(response);
      store.isUpdating = false;

      if (apply) {
        applySessionView(response.id);
      }

      return response;
    } catch (error) {
      store.isUpdating = false;
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  };

  const updateSessionView = async (options: {
    data: Pick<Filters.Filter, 'id'> & Partial<Filters.Filter>;
    apply?: boolean;
  }) => {
    try {
      const { data, apply } = options;

      store.isUpdatingId = options.data.id;
      const update = { ..._.omit(data, 'version'), filterVersion: ACTIVE_VERSION };
      const response = await HITL.filters.updateFilter({ ...update, version: 2 });

      setters.addOrUpdateView(response);
      store.isUpdatingId = '';

      if (apply) {
        applySessionView(response.id);
      }

      return response;
    } catch (error) {
      store.isUpdatingId = '';
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  };

  const bulkUpdateSessionViews = async (options: {
    data: Filters.BulkUpdateFiltersOptions;
  }) => {
    try {
      store.isUpdating = true;
      const response = await HITL.filters.bulkUpdateFilters({ ...options.data, version: 2 });

      for (const item of response.data) {
        setters.addOrUpdateView(item);
      }

      store.isUpdating = false;
      return response.data;
    } catch (error) {
      store.isUpdating = false;
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  };

  const deleteSessionView = async (options: {
    viewId: string;
    apply?: boolean;
  }) => {
    try {
      store.isDeletingId = options.viewId;
      await HITL.filters.deleteFilter({ id: options.viewId, version: 2 });

      setters.deleteView(options.viewId);
      store.isDeletingId = '';

      if (options.apply && options.viewId === activeSessionViewId.value) {
        applySessionView();
      }
    } catch (error) {
      store.isDeletingId = '';
      UIEventBus.$emit(THROW_API_ERROR, { error });
    }
  };

  return {
    initializeSessionViews,
    applySessionView,
    createSessionView,
    updateSessionView,
    bulkUpdateSessionViews,
    deleteSessionView,
  };
};
